import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  RefreshCw,
  History,
  FileText,
  GitCommitHorizontal,
  Diff,
  RotateCcw,
  Camera,
  ChevronDown,
} from 'lucide-react';

const isAllowedRollbackPath = (p) => {
  if (!p || typeof p !== 'string') return false;
  if (p === 'usr/local/hestia/conf/hestia.conf') return true;
  if (p === 'opt/fyreweb-monitoring/mail-check.sh') return true;
  if (p.startsWith('etc/nginx/')) return true;
  if (p.startsWith('etc/fail2ban/jail.d/')) return true;
  if (p.startsWith('etc/fail2ban/filter.d/')) return true;
  return false;
};

const StatusPill = ({ label }) => (
  <Badge className="bg-white/5 text-[#a1a1aa] border border-white/10 text-[10px]">
    {label}
  </Badge>
);

const DiffLine = ({ line }) => {
  if (line.startsWith('+') && !line.startsWith('+++')) {
    return <div className="text-emerald-400">{line}</div>;
  }
  if (line.startsWith('-') && !line.startsWith('---')) {
    return <div className="text-rose-400">{line}</div>;
  }
  if (line.startsWith('@@')) {
    return <div className="text-sky-400">{line}</div>;
  }
  if (line.startsWith('diff --git') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++')) {
    return <div className="text-[#e4e4e7]">{line}</div>;
  }
  return <div className="text-[#a1a1aa]">{line}</div>;
};

export function ConfigHistoryWidget() {
  const { authAxios } = useAuth();

  const [expanded, setExpanded] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [loadingHistory, setLoadingHistory] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const [loadingDiff, setLoadingDiff] = useState(false);
  const [diffData, setDiffData] = useState(null);

  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rollbackConfirm, setRollbackConfirm] = useState(false);
  const [rollbackUnderstand, setRollbackUnderstand] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  const fetchHistory = async (mode = 'initial') => {
    if (mode === 'initial') setLoadingHistory(true);
    if (mode === 'refresh') setRefreshing(true);

    try {
      const api = authAxios();
      const res = await api.get('/config/history');
      const nextItems = res.data?.items || [];
      setItems(nextItems);

      if (!selected && nextItems.length > 0) {
        setSelected(nextItems[0]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Config history konnte nicht geladen werden.');
    } finally {
      setLoadingHistory(false);
      setRefreshing(false);
    }
  };

  const fetchDiff = async (hash) => {
    if (!hash) return;
    setLoadingDiff(true);
    setDiffData(null);

    try {
      const api = authAxios();
      const res = await api.get('/config/diff', { params: { commit: hash } });
      setDiffData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Diff konnte nicht geladen werden.');
    } finally {
      setLoadingDiff(false);
    }
  };

  useEffect(() => {
    if (!expanded || hasLoadedOnce) return;
    fetchHistory('initial');
    setHasLoadedOnce(true);
  }, [expanded, hasLoadedOnce]);

  useEffect(() => {
    if (selected?.hash) {
      fetchDiff(selected.hash);
    }
  }, [selected?.hash]);

  const selectedFiles = useMemo(() => {
    const files = selected?.files || [];
    return files.map((p) => ({
      path: p,
      canRollback: isAllowedRollbackPath(p),
    }));
  }, [selected]);

  const diffLines = useMemo(() => {
    const text = diffData?.diff || '';
    const lines = text.split('\n');
    const maxLines = 2000;
    const truncated = lines.length > maxLines;
    return {
      lines: truncated ? lines.slice(0, maxLines) : lines,
      truncated,
      total: lines.length,
    };
  }, [diffData]);

  const runSnapshot = async () => {
    setSnapshotLoading(true);
    try {
      const api = authAxios();
      const res = await api.post('/config/snapshot', { confirm: true });
      const status = res.data?.status;
      if (status === 'ok') toast.success('Snapshot erstellt');
      else toast.error('Snapshot fehlgeschlagen');
      await fetchHistory('refresh');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Snapshot fehlgeschlagen.');
    } finally {
      setSnapshotLoading(false);
    }
  };

  const openRollback = (commitHash, path) => {
    setRollbackTarget({ commit: commitHash, path });
    setRollbackConfirm(false);
    setRollbackUnderstand(false);
  };

  const runRollback = async () => {
    if (!rollbackTarget?.commit || !rollbackTarget?.path) return;
    setRollbackLoading(true);
    try {
      const api = authAxios();
      const res = await api.post('/config/rollback', {
        commit: rollbackTarget.commit,
        path: rollbackTarget.path,
        confirm: true,
        i_understand: true,
      });
      if (res.data?.status === 'ok') {
        toast.success('Datei wiederhergestellt');
      } else {
        toast.error('Rollback fehlgeschlagen');
      }
      await fetchHistory('refresh');
      if (selected?.hash) await fetchDiff(selected.hash);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rollback fehlgeschlagen.');
    } finally {
      setRollbackLoading(false);
      setRollbackTarget(null);
    }
  };

  return (
    <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <History className="w-5 h-5 text-[#EF694C]" />
              Config History
            </h3>
            <p className="text-sm text-[#a1a1aa] mt-1">
              Lokale Git-Historie aus <span className="text-white">/opt/fyreweb-config-history</span>
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => setExpanded((v) => !v)}
              className="bg-white/5 hover:bg-white/10 text-white"
            >
              <ChevronDown className={`w-4 h-4 mr-2 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              {expanded ? 'Einklappen' : 'Ausklappen'}
            </Button>

            <Button
              onClick={() => fetchHistory('refresh')}
              disabled={!expanded || refreshing}
              className="bg-white/5 hover:bg-white/10 text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  disabled={!expanded || snapshotLoading}
                  className="bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Jetzt Snapshot
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#121214] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Snapshot erstellen?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#a1a1aa]">
                    Es wird ein lokaler Snapshot der Konfiguration erstellt (nur Backup, kein Remote-Push).
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={runSnapshot}
                    className="bg-[#EF694C] hover:bg-[#d65d43] text-white"
                  >
                    {snapshotLoading ? 'Erstelle…' : 'Ja, Snapshot erstellen'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {!expanded ? (
          <div className="mt-4 text-sm text-[#a1a1aa]">
            Eingeklappt, um das Dashboard kompakt zu halten.
          </div>
        ) : (
        loadingHistory ? (
          <div className="mt-4 flex items-center gap-3 text-white">
            <RefreshCw className="w-5 h-5 text-[#EF694C] animate-spin" />
            <div className="font-semibold">Config History wird geladen…</div>
          </div>
        ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                <GitCommitHorizontal className="w-4 h-4" />
                Letzte Commits
              </div>
              <StatusPill label={`${items.length} Einträge`} />
            </div>

            <div className="space-y-2">
              {items.map((it) => {
                const active = selected?.hash === it.hash;
                const dt = it.date ? new Date(it.date).toLocaleString() : '';

                return (
                  <button
                    key={it.hash}
                    onClick={() => setSelected(it)}
                    className={`w-full text-left rounded-xl border p-4 transition-colors ${
                      active
                        ? 'border-[#EF694C]/60 bg-[#EF694C]/[0.06]'
                        : 'border-white/10 bg-black/20 hover:border-[#EF694C]/40'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-white font-semibold text-sm truncate">{it.message || 'Commit'}</div>
                        <div className="text-xs text-[#a1a1aa] mt-1 truncate">
                          {it.hash} · {dt}
                        </div>
                      </div>
                      <Badge className="bg-white/5 text-[#a1a1aa] border border-white/10 text-[10px] whitespace-nowrap">
                        {(it.files || []).length} Dateien
                      </Badge>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Geänderte Dateien
                </div>
                {selected?.hash && <StatusPill label={selected.hash} />}
              </div>

              <div className="space-y-2">
                {selectedFiles.length === 0 && (
                  <div className="text-sm text-[#a1a1aa] bg-black/20 border border-white/10 rounded-xl p-4">
                    Keine Dateien für diesen Commit.
                  </div>
                )}

                {selectedFiles.map((f) => (
                  <div key={f.path} className="bg-black/20 border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="text-sm text-white truncate">{f.path}</div>
                    <div className="flex items-center gap-2">
                      {!f.canRollback && <StatusPill label="read-only" />}
                      {f.canRollback && (
                        <AlertDialog open={rollbackTarget?.path === f.path} onOpenChange={(open) => !open && setRollbackTarget(null)}>
                          <AlertDialogTrigger asChild>
                            <Button
                              onClick={() => openRollback(selected?.hash, f.path)}
                              className="bg-white/5 hover:bg-white/10 text-white"
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Wiederherstellen
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#121214] border-white/10 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Datei wiederherstellen?</AlertDialogTitle>
                              <AlertDialogDescription className="text-[#a1a1aa]">
                                Warnung: Diese Aktion überschreibt eine produktive Konfigurationsdatei auf dem Mail-Server und führt danach automatisch <span className="text-white">nginx -t</span> aus.
                                Bei Fehler wird automatisch zurückgerollt.
                              </AlertDialogDescription>
                            </AlertDialogHeader>

                            <div className="space-y-4">
                              <div className="text-sm">
                                Commit: <span className="font-semibold">{selected?.hash}</span>
                                <br />
                                Pfad: <span className="font-semibold">/{f.path}</span>
                              </div>

                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={rollbackConfirm}
                                    onCheckedChange={(v) => setRollbackConfirm(Boolean(v))}
                                  />
                                  <Label className="text-sm text-white">Ich bestätige, dass ich diese Datei wiederherstellen möchte.</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={rollbackUnderstand}
                                    onCheckedChange={(v) => setRollbackUnderstand(Boolean(v))}
                                  />
                                  <Label className="text-sm text-white">Ich verstehe das Risiko und habe den Diff geprüft.</Label>
                                </div>
                              </div>
                            </div>

                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">Abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={runRollback}
                                disabled={!rollbackConfirm || !rollbackUnderstand || rollbackLoading}
                                className="bg-[#F43F5E] hover:bg-[#e11d48] text-white"
                              >
                                {rollbackLoading ? 'Wiederherstelle…' : 'Ja, wiederherstellen'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white flex items-center gap-2">
                  <Diff className="w-4 h-4" />
                  Diff
                </div>
                {loadingDiff && (
                  <Badge className="bg-white/5 text-[#a1a1aa] border border-white/10">
                    <RefreshCw className="w-3 h-3 mr-2 animate-spin" />
                    Lade…
                  </Badge>
                )}
              </div>

              <Accordion type="single" collapsible defaultValue="stat">
                <AccordionItem value="stat" className="border-white/10">
                  <AccordionTrigger className="text-sm text-white">Stat</AccordionTrigger>
                  <AccordionContent>
                    <pre className="text-xs text-[#a1a1aa] bg-black/40 border border-white/10 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                      {(diffData?.stat || '—').trim()}
                    </pre>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="patch" className="border-white/10">
                  <AccordionTrigger className="text-sm text-white">Patch</AccordionTrigger>
                  <AccordionContent>
                    <div className="text-xs bg-black/40 border border-white/10 rounded-lg p-3 overflow-x-auto">
                      <div className="font-mono whitespace-pre">
                        {diffLines.lines.map((l, idx) => (
                          <DiffLine key={idx} line={l} />
                        ))}
                      </div>
                      {diffLines.truncated && (
                        <div className="mt-3 text-xs text-[#a1a1aa]">
                          Ausgabe gekürzt: {diffLines.lines.length}/{diffLines.total} Zeilen.
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
        ))}
      </CardContent>
    </Card>
  );
}
